import { useCallback, useEffect, useRef, useState } from 'react';

import {
  emitSocketV2ResearchAssistantEvent,
  getSocketV2ResearchAssistant,
  useAppSelector,
} from '@verantos-dev/app-state-base';
import { selectResearchAssistantMessages } from '@verantos-dev/app-state-research-assistant';
import { useSafeRouteParams } from '@verantos-dev/data-access-cohorts';
import { type FeedbackRating } from '@verantos-dev/shared-components';

/** Request state for async operations */
interface FeedbackRequestState {
  isSubmitting: boolean;
  isDeleting?: boolean;
  error?: string;
}

/** Feedback data combined with request state */
export interface FeedbackEntry extends FeedbackRequestState {
  rating: FeedbackRating | null;
  comment?: string;
}

export interface FeedbackState {
  [messageId: string]: FeedbackEntry;
}

export interface SubmitFeedbackOptions {
  userPrompt?: string;
  agentResponse?: string;
}

export interface UseFeedbackReturn {
  feedbackState: FeedbackState;
  submitFeedback: (
    messageId: string,
    rating: FeedbackRating,
    comment?: string,
    options?: SubmitFeedbackOptions
  ) => void;
  deleteFeedback: (messageId: string) => void;
}

export const useFeedback = (): UseFeedbackReturn => {
  const { projectId, cohortId, cohortVersionId } = useSafeRouteParams();
  const raMessages = useAppSelector(selectResearchAssistantMessages) ?? [];
  const [feedbackState, setFeedbackState] = useState<FeedbackState>({});

  const feedbackStateRef = useRef<FeedbackState>({});
  feedbackStateRef.current = feedbackState;

  // Helper to get langfuseTraceId from Redux messages
  const getLangfuseTraceId = useCallback(
    (messageId: string): string | undefined => {
      const message = raMessages.find((msg) => msg.messageId === messageId);
      return message?.langfuseTraceId ?? undefined;
    },
    [raMessages]
  );

  const submitFeedback = useCallback(
    (
      messageId: string,
      rating: FeedbackRating,
      comment?: string,
      options?: SubmitFeedbackOptions
    ) => {
      setFeedbackState((prev) => ({
        ...prev,
        [messageId]: {
          rating,
          comment,
          isSubmitting: true,
          isDeleting: false,
          error: undefined,
        },
      }));

      // Always derive langfuseTraceId from Redux messages (source of truth)
      const currentTraceId = getLangfuseTraceId(messageId);
      emitSocketV2ResearchAssistantEvent('feedback', {
        message_id: messageId,
        rating,
        comment,
        cohort_id: cohortId,
        project_id: projectId,
        cohort_version_id: cohortVersionId,
        user_prompt: options?.userPrompt,
        agent_response: options?.agentResponse,
        langfuse_trace_id: currentTraceId,
      });
    },
    [cohortId, projectId, cohortVersionId, getLangfuseTraceId]
  );

  const deleteFeedback = useCallback(
    (messageId: string) => {
      setFeedbackState((prev) => {
        const entry = prev[messageId];
        return {
          ...prev,
          [messageId]: {
            rating: entry?.rating ?? null,
            comment: entry?.comment,
            isSubmitting: true,
            isDeleting: true,
            error: undefined,
          },
        };
      });

      // Always derive langfuseTraceId from Redux messages (source of truth)
      const currentTraceId = getLangfuseTraceId(messageId);
      emitSocketV2ResearchAssistantEvent('feedback_delete', {
        message_id: messageId,
        cohort_id: cohortId,
        project_id: projectId,
        cohort_version_id: cohortVersionId,
        langfuse_trace_id: currentTraceId,
      });
    },
    [cohortId, projectId, cohortVersionId, getLangfuseTraceId]
  );

  useEffect(() => {
    let socket: Awaited<ReturnType<typeof getSocketV2ResearchAssistant>> | undefined;

    const handleFeedbackSubmitted = (data: {
      message_id: string;
      rating: FeedbackRating;
      comment?: string;
      status: string;
      langfuse_trace_id?: string | null;
    }) => {
      setFeedbackState((prev) => ({
        ...prev,
        [data.message_id]: {
          rating: data.rating,
          comment: data.comment,
          isSubmitting: false,
          isDeleting: false,
        },
      }));
    };

    const handleFeedbackDeleted = (data: { message_id: string }) => {
      setFeedbackState((prev) => ({
        ...prev,
        [data.message_id]: {
          rating: null,
          comment: undefined,
          isSubmitting: false,
          isDeleting: false,
          error: undefined,
        },
      }));
    };

    const handleFeedbackError = (data: { message_id: string; error: string }) => {
      setFeedbackState((prev) => {
        const existing = prev[data.message_id];
        return {
          ...prev,
          [data.message_id]: {
            rating: existing?.rating ?? null,
            comment: existing?.comment,
            isSubmitting: false,
            isDeleting: false,
            error: data.error,
          },
        };
      });
    };

    const setupListeners = async () => {
      try {
        socket = await getSocketV2ResearchAssistant('/agent');
        if (socket) {
          socket.on('feedback_submitted', handleFeedbackSubmitted);
          socket.on('feedback_deleted', handleFeedbackDeleted);
          socket.on('feedback_error', handleFeedbackError);
        }
      } catch {
        // no-op - safe to ignore
      }
    };

    setupListeners();

    return () => {
      if (socket) {
        socket.off('feedback_submitted', handleFeedbackSubmitted);
        socket.off('feedback_deleted', handleFeedbackDeleted);
        socket.off('feedback_error', handleFeedbackError);
      }
    };
  }, []);

  return {
    feedbackState,
    submitFeedback,
    deleteFeedback,
  };
};
